import React, { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { getCategory, deleteCategory } from "../../../services/admin/category";

interface Category {
    id: number;
    name: string;
}

function AdminCategory() {
    const navigate = useNavigate();
    const [category, setCategory] = useState<Category[]>([]);
    const [search, setSearch] = useState("");
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const loadCategory = async () => {
        setIsLoading(true);
        const res = await getCategory();
        if (res.success && Array.isArray(res.data)) {
            setCategory(res.data);
        } else {
            console.error("Failed to load category", res.error || res.data);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        loadCategory();
    }, []);

    const handleDeleteClick = (category: Category) => {
        setSelectedCategory(category);
        setShowConfirm(true);
    };

    const confirmDelete = async () => {
        if (selectedCategory) {
            const res = await deleteCategory(selectedCategory.id);
            if (res.success) {
                setCategory((prev) => prev.filter((b) => b.id !== selectedCategory.id));
            } else {
                console.error("Delete failed:", res.error);
            }
            setShowConfirm(false);
            setSelectedCategory(null);
        }
    };

    const handleEditClick = (category: Category) => {
        navigate(`/admin/category/edit-category/${category.id}`);
    };

    const filteredCategory = category.filter((category) =>
        category.name.toLowerCase().includes(search.toLowerCase())
    );

    const columns: TableColumn<Category>[] = [
        {
            name: "ID",
            cell: (_row, index) => (index + 1).toString(),
            sortable: true,
        },
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => handleEditClick(row)}
                        className="text-[#009FE3] hover:underline"
                        title="Edit"
                    >
                        <Pencil size={16} />
                    </button>
                    <button
                        onClick={() => handleDeleteClick(row)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            ),
            ignoreRowClick: true,
        },
    ];

    return (
        <div className="p-6 space-y-6">
            <h1 className="text-2xl font-semibold text-gray-800">Category</h1>

            {/* Search + Add Category */}
            <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="relative w-full max-w-sm">
                    <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search category..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#009FE3]"
                    />
                </div>

                <Link
                    to="add-category"
                    className="flex items-center gap-2 px-4 py-2 bg-[#009FE3] text-white rounded-lg hover:opacity-90 transition whitespace-nowrap"
                >
                    <Plus className="w-4 h-4" />
                    Add Category
                </Link>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-lg shadow border">
                <DataTable
                    columns={columns}
                    data={filteredCategory}
                    progressPending={isLoading}
                    pagination
                    highlightOnHover
                    responsive
                    striped
                    noHeader
                />
            </div>

            {/* Confirm Delete Modal */}
            {showConfirm && selectedCategory && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
                        <h2 className="text-lg font-semibold mb-4">Confirm Deletion</h2>
                        <p className="mb-4">
                            Are you sure you want to delete{" "}
                            <strong>{selectedCategory.name}</strong>?
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowConfirm(false);
                                    setSelectedCategory(null);
                                }}
                                className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminCategory;
